// The stored records, read from and written to Firestore's document data. Every collection the web reads has a
// decoder that never throws (an unreadable document is an error result, skipped and reported, never rewritten) and an
// encoder that writes the record as Swift's encoder does, its unknown fields included.

import type { DecodeContext, Decoded, EncodeOptions, Fields } from "./values";
import { decodeTodo, encodeTodo, type Todo } from "./todo";
import {
  decodeArea, decodeInboxTrace, decodeProject, decodeSection, encodeArea, encodeInboxTrace, encodeProject, encodeSection,
  type Area, type InboxTrace, type Project, type Section,
} from "./project";
import { decodeHabit, encodeHabit, type Habit } from "./habit";
import { decodeAttachment, decodeTemplate, encodeAttachment, encodeTemplate, type Attachment, type Template } from "./template";
import {
  decodeBirthday, decodePlace, decodeSituation, encodeBirthday, encodePlace, encodeSituation,
  type Birthday, type Place, type Situation,
} from "./situation";

export * from "./values";
export * from "./shared";
export * from "./todo";
export * from "./project";
export * from "./template";
export * from "./habit";
export * from "./situation";
export * from "./user";
export * from "./share";

/** How the records of one collection are read and written. */
export interface RecordCodec<T extends { id: string }> {
  decode(data: Fields, context: DecodeContext): Decoded<T>;
  encode(record: T, options?: EncodeOptions): Fields;
}

/** The records of each collection under `users/{uid}` (and, for the first four, under `shares/{id}`), by its name. */
export interface RecordTypes {
  todos: Todo;
  projects: Project;
  areas: Area;
  sections: Section;
  templates: Template;
  habits: Habit;
  attachments: Attachment;
  birthdays: Birthday;
  places: Place;
  situations: Situation;
  inboxTraces: InboxTrace;
}

export type CollectionName = keyof RecordTypes;

export const codecs: { [Name in CollectionName]: RecordCodec<RecordTypes[Name]> } = {
  todos: { decode: decodeTodo, encode: encodeTodo },
  projects: { decode: decodeProject, encode: encodeProject },
  areas: { decode: decodeArea, encode: encodeArea },
  sections: { decode: decodeSection, encode: encodeSection },
  templates: { decode: decodeTemplate, encode: encodeTemplate },
  habits: { decode: decodeHabit, encode: encodeHabit },
  attachments: { decode: decodeAttachment, encode: encodeAttachment },
  birthdays: { decode: decodeBirthday, encode: encodeBirthday },
  places: { decode: decodePlace, encode: encodePlace },
  situations: { decode: decodeSituation, encode: encodeSituation },
  inboxTraces: { decode: decodeInboxTrace, encode: encodeInboxTrace },
};

/** The collections a share holds (FirestoreBackend.shareable). */
export const SHAREABLE_COLLECTIONS: readonly CollectionName[] = ["todos", "projects", "areas", "sections"];
